import React, { createContext, useContext } from 'react';
import { useTheme } from '@mui/material/styles';

interface IconsContextType {
  // Legacy props (keep for backward compatibility)
  iconPropsLarge: { width: number; height: number };
  iconPropsSmall: { width: number; height: number };
  boxPropsGreen: any;
  boxPropsBlue: any;

  // New comprehensive props by iconClass
  iconPropsTree: any;
  boxPropsTree: any;
  iconPropsHeadline: any;
  boxPropsHeadline: any;
  iconPropsEventHeadline: any;
  boxPropsEventHeadline: any;
  iconPropsMenu: any;
  boxPropsMenu: any;
  iconPropsReportToolbar: any;
  boxPropsReportToolbar: any;
  iconPropsMapToolbar: any;
  boxPropsMapToolbar: any;
  iconPropsConfirmDialog: any;
  boxPropsConfirmDialog: any;
  iconPropsBanner: any;
  boxPropsBanner: any;
}

const IconsContext = createContext<IconsContextType | undefined>(undefined);

export const IconsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();

  // Legacy props
  const iconPropsLarge = { width: 40, height: 40 };
  const iconPropsSmall = { width: 20, height: 20 };
  const boxPropsGreen = {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    border: '1px solid black', borderRadius: '5px',
    backgroundColor: `${theme.palette.warning.light}35`, p: 1,
    '--mui-icon-fill-color': `${theme.palette.success.main}20`,
    color: `${theme.palette.success.light}20`,
    '&:hover': { backgroundColor: `${theme.palette.warning.main}80` }
  };
  const boxPropsBlue = {
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    border: '1px solid black', borderRadius: '5px',
    backgroundColor: `${theme.palette.warning.light}35`, p: 1,
    '--mui-icon-fill-color': `${theme.palette.primary.main}20`,
    color: `${theme.palette.primary.light}20`,
    '&:hover': { backgroundColor: `${theme.palette.warning.main}80` }
  };

  // Tree icons (TreeOverview)
  const iconPropsTree = { width: 24, height: 24 };
  const boxPropsTree = { mr: 1, '--mui-icon-fill-color': `${theme.palette.warning.light}35`, color: `${theme.palette.grey[500]}` };

  // Headline icons (large display)
  const iconPropsHeadline = { width: 32, height: 32, color: theme.palette.primary.main };
  const boxPropsHeadline = { display: 'flex', justifyContent: 'center', alignItems: 'center' };

  // Headline icons (smaller display)
  const iconPropsEventHeadline = { width: 24, height: 24, color: theme.palette.primary.main };
  const boxPropsEventHeadline = { display: 'flex', justifyContent: 'center', alignItems: 'center' };

  // Menu icons (context menus)
  const iconPropsMenu = { fontSize: 24 };
  const boxPropsMenu = {};

  // Report toolbar icons
  const iconPropsReportToolbar = { fontSize: 24 };
  const boxPropsReportToolbar = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 'var(--toolbar-action-size)',
    width: 'var(--toolbar-action-size)',
    backgroundColor: 'var(--toolbar-action-surface)',
    color: 'var(--toolbar-action-text)',
    borderRadius: 'var(--toolbar-action-radius)',
    '&:hover': { backgroundColor: 'var(--toolbar-action-hover)' },
    cursor: 'pointer'
  };

  // Map toolbar icons
  const iconPropsMapToolbar = { fontSize: 24 };
  const boxPropsMapToolbar = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 'var(--toolbar-action-size)',
    width: 'var(--toolbar-action-size)',
    backgroundColor: 'var(--toolbar-action-surface)',
    color: 'var(--toolbar-action-text)',
    borderRadius: 'var(--toolbar-action-radius)',
    '&:hover': { backgroundColor: 'var(--toolbar-action-hover)' },
    cursor: 'pointer'
  };

  // Confirm dialog icons
  const iconPropsConfirmDialog = { fontSize: 24 };
  const boxPropsConfirmDialog = {};

  // Banner icons
  const iconPropsBanner = { fontSize: 40 };
  const boxPropsBanner = {};

  return (
    <IconsContext.Provider value={{
      iconPropsLarge, iconPropsSmall, boxPropsGreen, boxPropsBlue,
      iconPropsTree, boxPropsTree,
      iconPropsHeadline, boxPropsHeadline,
      iconPropsEventHeadline, boxPropsEventHeadline,
      iconPropsMenu, boxPropsMenu,
      iconPropsReportToolbar, boxPropsReportToolbar,
      iconPropsMapToolbar, boxPropsMapToolbar,
      iconPropsConfirmDialog, boxPropsConfirmDialog,
      iconPropsBanner, boxPropsBanner
    }}>
      {children}
    </IconsContext.Provider>
  );
};

export const useIconsContext = () => {
  const context = useContext(IconsContext);
  if (!context) {
    throw new Error('useIconsContext must be used within an IconsProvider');
  }
  return context;
};

export { IconsContext };
